import React, { useState } from 'react';
import { Alert, Button, Flex, Modal, Tag, Typography, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { RightCircleOutlined } from '@ant-design/icons';
import {
  ApplyToInternship,
  GetInternshipOfferById,
} from '../../helper/apiHelper';
import type { DetailedInternship } from '../../Types/interfaces';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '../../Constants/queryKeys';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useProcessStartSettings } from '../../hooks/useProcessStartSettings';
import { ProcessBlockedBanner } from '../ProcessBlockedBanner';
import { formatDate, parseDate } from '../../Utils/date';

export type ApplyableStatus =
  | 'Allowed'
  | 'HisOwnCompany'
  | 'HeIsntOnThatStudyLine';

interface InternshipApplicationComponentProps {
  internshipId: number;
  sessionId: number;
  organization: string;
  removeInternship: (id: number) => void;
  applyable: ApplyableStatus;
  studyLineName: string;
}

const GetDateOnlyFromDateTime = (dateTime: Date): string => {
  const date = parseDate(dateTime);
  // check if date is not 01/01/1000
  if (!date || date.year() === 1) {
    return 'N/A';
  }
  return formatDate(dateTime);
};

const InternshipApplicationComponent = ({
  internshipId,
  sessionId,
  organization,
  removeInternship,
  applyable,
  studyLineName,
}: InternshipApplicationComponentProps): React.ReactElement => {
  const loggedInUser = useCurrentUser();
  const [open, setOpen] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { internshipApplicationEnabled } = useProcessStartSettings();
  const applyButtonDisabled =
    applyable !== 'Allowed' || !internshipApplicationEnabled;

  const { data: internship } = useQuery({
    queryKey: [...QUERY_KEYS.INTERNSHIP, internshipId],
    queryFn: async () => {
      return await GetInternshipOfferById(internshipId.toString());
    },
  });

  const showModal = (): void => {
    setOpen(true);
  };

  const applyMutation = useMutation({
    mutationFn: async () => {
      await ApplyToInternship(
        String(loggedInUser?.id),
        internship ? internship.id.toString() : '0',
        sessionId
      );
    },
    onSuccess: () => {
      setOpen(false);
      removeInternship(internship ? internship.id : 0);
      messageApi.open({
        type: 'success',
        content: t('internshipApplicationComp.applySuccess'),
      });

      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.STUDENT_DATA });
    },
    onError: () => {
      messageApi.open({
        type: 'error',
        content: t('internshipApplicationComp.applyError'),
      });
    },
  });

  const isPaid = (internship: DetailedInternship | undefined): string => {
    if (internship) {
      return internship.paid
        ? t('internshipApplicationComp.yes')
        : t('internshipApplicationComp.no');
    }
    return t('internshipApplicationComp.na');
  };

  const warningApplyableMessage = (): React.ReactNode => {
    switch (applyable) {
      case 'HisOwnCompany':
        return (
          <Alert
            showIcon
            message={t(
              'internshipApplicationComp.youCannotApplyForYourOwnInternship'
            )}
            type="warning"
          />
        );

      case 'HeIsntOnThatStudyLine':
        return (
          <Alert
            showIcon
            message={
              t('internshipApplicationComp.alertMessage') +
              `: ${studyLineName}.`
            }
            type="warning"
          />
        );
      default:
        return null;
    }
  };

  const na = t('internshipApplicationComp.na');
  const sessionNames = internship
    ? Array.from(
        new Set(internship.internshipSessions.map((s) => s.description))
      )
    : [];

  const details: { label: string; value: React.ReactNode }[] = [
    { label: t('internshipApplicationComp.organization'), value: organization },
    {
      label: t('internshipApplicationComp.descr'),
      value: internship?.description || na,
    },
    {
      label: t('internshipApplicationComp.period'),
      value: internship
        ? `${GetDateOnlyFromDateTime(internship.startTime)} – ${GetDateOnlyFromDateTime(internship.endTime)}`
        : na,
    },
    {
      label: t('internshipApplicationComp.paid'),
      value: internship ? (
        <Tag color={internship.paid ? 'success' : 'default'} bordered={false}>
          {isPaid(internship)}
        </Tag>
      ) : (
        na
      ),
    },
    {
      label: t('internshipApplicationComp.applNum'),
      value: internship ? internship.numberOfPositions : 0,
    },
    {
      label: t('internshipApplicationComp.internshipSessions'),
      value: sessionNames.length ? (
        <Flex gap={4} wrap>
          {sessionNames.map((name) => (
            <Tag key={name} bordered={false}>
              {name}
            </Tag>
          ))}
        </Flex>
      ) : (
        '-'
      ),
    },
  ];

  return (
    <>
      {contextHolder}
      <Button size="large" type="text" onClick={showModal}>
        {t('internshipApplicationComp.lrnMore')}
        <RightCircleOutlined />
      </Button>
      <Modal
        title={internship ? internship.name : na}
        open={open}
        width={560}
        onCancel={() => setOpen(false)}
        key={internshipId}
        footer={
          <Flex justify="flex-end" align="center" gap={12}>
            {/* Warning message if student cannot apply to this internship */}
            <div style={{ flex: 1, textAlign: 'left' }}>
              {warningApplyableMessage()}
            </div>
            <Button
              disabled={applyButtonDisabled}
              type="primary"
              loading={applyMutation.isPending}
              onClick={() => applyMutation.mutate()}
            >
              {t('internshipApplicationComp.apply')}
            </Button>
          </Flex>
        }
      >
        {!internshipApplicationEnabled && (
          <ProcessBlockedBanner
            style={{ marginBottom: 16 }}
            message={t('processStartSettings.internshipApplicationBlocked')}
          />
        )}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(110px, auto) 1fr',
            gap: '12px 16px',
            paddingTop: 8,
          }}
        >
          {details.map(({ label, value }) => (
            <React.Fragment key={label}>
              <Typography.Text type="secondary">{label}</Typography.Text>
              <div style={{ whiteSpace: 'pre-wrap', fontWeight: 500 }}>
                {value}
              </div>
            </React.Fragment>
          ))}
        </div>
      </Modal>
    </>
  );
};

export default InternshipApplicationComponent;
